import { create } from 'zustand';
import { Subscription } from 'rxjs';
import { queryClient } from '@warehouse/shared/infra';
import { refetchNxObject } from '@warehouse/object-browser/infra';
import {
	brokerJobsQueryKey,
	BrokerJob,
	BrokerService,
	WatchJobsResult,
	CANCELLABLE_BROKER_JOB_STATUSES,
} from '../core';
import { brokerJobsRepositorySingleton } from '../infra';

export interface BrokerJobsStore {
	jobsByNxObject: Record<string, BrokerJob[]>;
	loadingByNxObject: Record<string, boolean>;
	fetchErrorByNxObject: Record<string, boolean>;
	refreshingByNxObject: Record<string, boolean>;
	cancelConfirmJobId: string | null;
	actions: {
		refresh(nxObjectUuid: string): void;
		triggerService(nxObjectUuid: string, service: BrokerService): Promise<{ jobId: string }>;
		askCancelJob(jobId: string | null): void;
		confirmCancelJob(nxObjectUuid: string, jobId: string): Promise<void>;
	};
	internalActions: {
		subscribe(nxObjectUuid: string): void;
		unsubscribe(nxObjectUuid: string): void;
	};
}

/**
 * A job that has just succeeded may have written to the object — Mezz Proxy's proxy, Media Info's metadata — which
 * the Inventory API returns with it, so the object is refetched, whoever started the job and whichever tab is open.
 */
function invalidateNxObjectIfJobJustSucceeded(nxObjectUuid: string, previousJobs: BrokerJob[], jobs: BrokerJob[]) {
	const previousStatusByJobId = new Map(previousJobs.map((job) => [job.jobId, job.status]));
	const jobJustSucceeded = jobs.some(
		(job) => job.status === 'SUCCEEDED' && previousStatusByJobId.get(job.jobId) !== 'SUCCEEDED',
	);
	if (jobJustSucceeded) {
		refetchNxObject(nxObjectUuid);
	}
}

const subscriptionsByNxObject = new Map<string, { subscription: Subscription; refCount: number }>();

export const useBrokerJobsStore = create<BrokerJobsStore>((set, get) => {
	const handleWatchResult = (nxObjectUuid: string, result: WatchJobsResult) => {
		const previous = get();
		// Only a list that has loaded before can tell a job that just succeeded from one that always had.
		if (previous.loadingByNxObject[nxObjectUuid] === false && result.data) {
			invalidateNxObjectIfJobJustSucceeded(nxObjectUuid, previous.jobsByNxObject[nxObjectUuid] ?? [], result.data);
		}
		set((state) => ({
			jobsByNxObject: {
				...state.jobsByNxObject,
				[nxObjectUuid]: result.data ?? state.jobsByNxObject[nxObjectUuid] ?? [],
			},
			loadingByNxObject: { ...state.loadingByNxObject, [nxObjectUuid]: result.isLoading },
			fetchErrorByNxObject: { ...state.fetchErrorByNxObject, [nxObjectUuid]: result.isError },
			refreshingByNxObject: { ...state.refreshingByNxObject, [nxObjectUuid]: result.isFetching },
		}));
	};

	const refresh = (nxObjectUuid: string) => {
		queryClient.refetchQueries({ queryKey: brokerJobsQueryKey(nxObjectUuid) });
	};

	return {
		jobsByNxObject: {},
		loadingByNxObject: {},
		fetchErrorByNxObject: {},
		refreshingByNxObject: {},
		cancelConfirmJobId: null,
		actions: {
			refresh,
			async triggerService(nxObjectUuid, service) {
				try {
					const { jobId } = await brokerJobsRepositorySingleton.get()
						.submitJob({ nxObjectUuid, serviceUUID: service.serviceUUID });
					refresh(nxObjectUuid);
					return  { jobId }; 
				} catch (error) {
					console.error(error);
					return { jobId: 'error'};
				}
			},
			askCancelJob(jobId) {
				set({ cancelConfirmJobId: jobId });
			},
			async confirmCancelJob(nxObjectUuid, jobId) {
				set({ cancelConfirmJobId: null });
				try {
					await brokerJobsRepositorySingleton.get().cancelJob(jobId);
					refresh(nxObjectUuid);
				} catch (error) {
					console.error(error);
				}
			},
		},
		internalActions: {
			subscribe(nxObjectUuid) {
				const existing = subscriptionsByNxObject.get(nxObjectUuid);
				if (existing) {
					existing.refCount += 1;
					return;
				}

				const subscription = brokerJobsRepositorySingleton.get()
					.watchJobsForNxObject({ nxObjectUuid })
					.subscribe((result) => handleWatchResult(nxObjectUuid, result));
				subscriptionsByNxObject.set(nxObjectUuid, { subscription, refCount: 1 });
			},
			unsubscribe(nxObjectUuid) {
				const existing = subscriptionsByNxObject.get(nxObjectUuid);
				if (!existing) return;

				existing.refCount -= 1;
				if (existing.refCount <= 0) {
					existing.subscription.unsubscribe();
					subscriptionsByNxObject.delete(nxObjectUuid);
				}
			},
		},
	};
});

export function isJobCancellable(job: BrokerJob): boolean {
	return CANCELLABLE_BROKER_JOB_STATUSES.includes(job.status);
}

export const brokerJobsStoreSelector = {
	jobsFor: (nxObjectUuid: string) => (state: BrokerJobsStore) => state.jobsByNxObject[nxObjectUuid] ?? [],
	isLoading: (nxObjectUuid: string) => (state: BrokerJobsStore) => state.loadingByNxObject[nxObjectUuid] ?? true,
	isFetchError: (nxObjectUuid: string) => (state: BrokerJobsStore) => state.fetchErrorByNxObject[nxObjectUuid] ?? false,
	isRefreshing: (nxObjectUuid: string) => (state: BrokerJobsStore) => state.refreshingByNxObject[nxObjectUuid] ?? false,
	cancelConfirmJobId: (state: BrokerJobsStore) => state.cancelConfirmJobId,
	actions: (state: BrokerJobsStore) => state.actions,
	internalActions: (state: BrokerJobsStore) => state.internalActions,
};
